import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './MessageItem.module.scss';
import classes from 'classnames';
import DOMPurify from 'dompurify';
import SVG from 'react-inlinesvg';
import TutorAvatarIcon from '@/components/ConversationWithTutor/TutorAvatarIcon/TutorAvatarIcon';
import lightbulbIcon from '@/assets/svg/lightbulb.svg';
import {
  TutorChatMessage,
  TutorChatSender,
  AzureTTS,
  AzureTTSInputFormat,
  TutorChatLevel,
  TutorChatStyle,
  TutorChatL1,
} from '@mavatar/melingo-voice-and-avatar-sdk';
import QuiFromDashboard from '@/components/TutorDashboard/QuiFromDashboard/QuiFromDashboard';
import PlayMessageButton from '@/components/ConversationWithTutor/PlayMessageButton/PlayMessageButton';
import UseAzureLanguageSettings from '@/hooks/UseAzureLanguageSettings';
import { formatDateToStandard } from '@/utils/formatters';
import TutorTopicsUtils from '@/utils/tutorTopics';

export interface TopicProps {
  style: TutorChatStyle;
  text: string;
  topic: string;
  user_role: string;
  system_role: string;
  level: TutorChatLevel;
  tempData?: any;
  extraField?: {
    ellLevel: string;
    ellUnit: string;
    ellTopic?: string;
    ellActivity?: string;
  };
}

export interface TopicsGroup {
  title?: string;
  topics: TopicProps[];
}

export interface QuisGroup {
  question: string;
  answers: string[];
  correctAnswer: string;
}

export interface MessageItemProps {
  message: TutorChatMessage;
  topicsGroups?: TopicsGroup[];
  quisGroups?: QuisGroup[];
  azureTTS?: AzureTTS;
  showSpeaker: boolean;
  voice: string;
  isMuted?: boolean;
  showNameAndTime?: boolean;
  showTuorIcon?: boolean;
  handleTopic?: (topic: TopicProps) => void;
  onQuizComplete?: (correctAnswers: number, numberOfQuestions: number) => void;
  onWritingChange?: (isWriting: boolean, element?: HTMLElement | null, hasTopics?: boolean) => void;
  userName?: string;
}

export const getSsmlFormat = (
  _text: string | undefined,
  _lang: TutorChatL1,
  ttsVoices: { [lang: string]: string }
) => {
  const lang: string =
    _lang === TutorChatL1.PT
      ? 'pt-BR'
      : _lang === TutorChatL1.SP
        ? 'es-ES'
        : _lang === TutorChatL1.HE
          ? 'he-IL'
          : 'en-US';

  const phoneme: any = {
    'en-US': {
      i: 'aɪ',
      a: 'eɪ',
      o: 'oʊ',
      u: 'juː',
      b: 'bi',
      t: 'ti',
      x: 'ɛks',
    },
    'pt-BR': {
      a: 'a',
      à: 'a',
      e: 'i',
      é: 'ɛ',
      o: 'u',
      ó: 'ɔ',
      ô: 'o',
    },
    'es-ES': {
      a: 'a',
      á: 'a',
      e: 'e',
      é: 'e',
      y: 'i',
      o: 'o',
      ó: 'o',
    },
  };

  const normalSsmlText = (text: string) => {
    return text.replaceAll('&', 'and');
  };

  const checkVowels = (text: string) => {
    const cleanedString = (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f\W_]/g, '');

    if (cleanedString.length === 0) {
      return [false, false];
    }

    const vowels = new Set(['a', 'e', 'i', 'o', 'u']);

    const startsWithVowel = vowels.has(cleanedString[0]);
    const endsWithVowel = vowels.has(cleanedString[cleanedString.length - 1]);

    return [startsWithVowel, endsWithVowel];
  };

  const keepLettersNumbersAndDiacritics = (text: string) => {
    return text.replace(/[^\p{L}\p{N}\p{M}]/gu, '').toLocaleLowerCase();
  };

  const splitTextByQuotes = (text: string) => {
    const _text = ` ${text} `;

    const partsTexts = [''];
    for (let index = 0; index < _text.length; index++) {
      if (
        _text[index] === "'" &&
        (!_text[index - 1].replace(/[^\p{L}\p{M}]/gu, '') ||
          !_text[index + 1].replace(/[^\p{L}\p{M}]/gu, ''))
      ) {
        partsTexts.push('');
      } else {
        partsTexts[partsTexts.length - 1] = partsTexts[partsTexts.length - 1] + _text[index];
      }
    }

    return partsTexts;
  };

  const ssmlMultiLanguageFormat = () => {
    //const partsTexts = ` ${normalSsmlText(_text)} `.split(/\s'|'\s/);
    const partsTexts = splitTextByQuotes(normalSsmlText(_text || ''));
    const parts = partsTexts
      .map((part, i) => {
        const t = part.trim();

        if (!t) {
          return '';
        }

        const phonemeBreakTime = '<break time="1ms"/>';
        let breakTime = '';
        if (i < partsTexts.length - 1 && checkVowels(t)[1] && checkVowels(partsTexts[i + 1])[0]) {
          breakTime = '<break time="1ms"/>';
        }

        const cleanPhoneme = keepLettersNumbersAndDiacritics(t);

        if (i % 2 === 0) {
          if (phoneme[lang]?.[cleanPhoneme]) {
            return `${phonemeBreakTime}<phoneme alphabet="ipa" ph="${phoneme[lang][cleanPhoneme]}">${t}</phoneme>${phonemeBreakTime}`;
          }

          return `${t}${breakTime}`;
        }

        const _t = t.replaceAll('.', ' ').replaceAll(',', ' ').trim();

        if (phoneme['en-US'][cleanPhoneme]) {
          return `${phonemeBreakTime}<phoneme alphabet="ipa" ph="${phoneme['en-US'][cleanPhoneme]}">${_t}</phoneme>${phonemeBreakTime}`;
        }

        return `<lang xml:lang="en-US">${_t}${breakTime}</lang>`;

        // f (i % 2 === 0) {
        //  return `<voice name="${ttsVoices[lang]}"><lang xml:lang="${lang}">${t}${breakTime}</lang></voice>`;
        //
        // // return `<s><lang xml:lang="en-US">'<emphasis level="strong">${t}</emphasis>'</lang></s>`;
        // eturn `<voice name="${voice}"><lang xml:lang="en-US">${t}${breakTime}</lang></voice>`;
      })
      .filter(part => !!part)
      .join(' ')
      .replace(/\n+/g, '<break strength="medium"/>');

    return `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="https://www.w3.org/2001/mstts" xml:lang="${lang}"><voice name="${ttsVoices[lang]}"><prosody rate="0.97">${parts}</prosody></voice></speak>`;
    // return `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="https://www.w3.org/2001/mstts" xml:lang="${lang}">${parts}</speak>`;
  };

  const ssmlFormat = () => {
    const partsTexts = splitTextByQuotes(normalSsmlText(_text || ''));
    const parts = partsTexts
      .map((part, i) => {
        const t = part.trim();

        if (!t) {
          return '';
        }

        const apostrophe = i % 2 === 0 ? `` : `"`;

        const cleanPhoneme = keepLettersNumbersAndDiacritics(t);

        if (phoneme['en-US']?.[cleanPhoneme]) {
          return `${apostrophe}<phoneme alphabet="ipa" ph="${phoneme['en-US'][cleanPhoneme]}">${part}</phoneme>${apostrophe}`;
        }

        return `${apostrophe}${part}${apostrophe}`;
      })
      .filter(part => !!part)
      .join('')
      .trim()
      .replace(/\n+/g, '<break strength="medium"/>');

    return `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xmlns:mstts="https://www.w3.org/2001/mstts" xml:lang="en-US"><voice name="${ttsVoices['en-US']}"><prosody rate="0.97">${parts}</prosody></voice></speak>`;
  };

  const textFormat = () => {
    return _text;
  };

  const inputFormat = AzureTTSInputFormat.SSML; // _lang === TutorChatL1.EN ? AzureTTSInputFormat.TEXT : AzureTTSInputFormat.SSML;
  const text = _lang === TutorChatL1.EN ? ssmlFormat() : ssmlMultiLanguageFormat();

  return {
    lang,
    text,
    inputFormat,
  };
};

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  topicsGroups,
  quisGroups,
  azureTTS,
  showSpeaker,
  voice,
  isMuted = false,
  showNameAndTime = true,
  showTuorIcon = true,
  handleTopic,
  onQuizComplete,
  onWritingChange,
  userName = 'User',
}) => {
  const { ttsVoices } = UseAzureLanguageSettings();
  const [showCorrection, setShowCorrection] = useState(false);
  const [isSpeaked, setIsSpeaked] = useState(false);
  const [startOnMute, setStartOnMute] = useState(false);
  const [isPreSpeaking, setIsPreSpeaking] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSpeakTextReady, setIsSpeakTextReady] = useState(false);
  const isActiveRef = useRef<boolean>(true);

  useEffect(() => {
    isActiveRef.current = true;

    return () => {
      isActiveRef.current = false;
    };
  }, []);

  const handleSpeak = useCallback(
    (_text: string | undefined, _lang: TutorChatL1, abort: boolean) => {
      if (!_text || !azureTTS || azureTTS?.audioQueueManager?.muted) return;

      const { lang, text, inputFormat } = getSsmlFormat(_text, _lang, ttsVoices);

      const play = async () => {
        if (abort) {
          await azureTTS.abort();
        }

        if (!isActiveRef.current) {
          return;
        }

        setIsPreSpeaking(true);
        azureTTS
          .addToQueue(text, {
            language: lang,
            inputFormat: inputFormat,
            onStartPlay: () => {
              if (!isActiveRef.current) {
                return;
              }

              setIsSpeaking(true);
              setIsPreSpeaking(false);
            },
          })
          .finally(() => {
            if (!isActiveRef.current) {
              return;
            }

            setIsSpeaked(true);
            setIsSpeaking(false);
            setIsPreSpeaking(false);
          });
      };

      play();
    },
    [azureTTS, ttsVoices]
  );

  const onTopicClick = useCallback(
    (topic: TopicProps) => {
      if (handleTopic) {
        handleTopic(topic);
      }
    },
    [handleTopic]
  );

  const getSpeakerIcon = useCallback(
    (text: string, lang: TutorChatL1) => {
      if (!showSpeaker || !text) {
        return null;
      }

      const onClick = isMuted
        ? () => {
            if (azureTTS?.audioQueueManager) {
              azureTTS.audioQueueManager.muted = false;
              handleSpeak(text, lang, true);
            }
          }
        : isPreSpeaking || isSpeaking
          ? () => {
              azureTTS?.abort();
            }
          : () => {
              handleSpeak(text, lang, true);
            };

      return (
        <PlayMessageButton
          className={styles.speakerIcon}
          // disabled={isMuted}
          loading={isPreSpeaking}
          speaking={isSpeaking}
          speaked={isSpeaked}
          onClick={onClick}
        />
      );
    },
    [showSpeaker, isSpeaked, isPreSpeaking, isSpeaking, isMuted, handleSpeak]
  );

  const getTime = useCallback((_date?: Date) => {
    const now = new Date();
    const date = _date || now;

    // const nowDay = `${now.getDate()}/${now.getMonth() + 1}/${now.getFullYear()}`;
    // const dateDay = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    const nowDay = formatDateToStandard(now);
    const dateDay = formatDateToStandard(date);
    const dateTime = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;

    return nowDay === dateDay ? dateTime : `${dateDay} ${dateTime}`;
  }, []);

  useEffect(() => {
    if (
      showSpeaker &&
      !message.disableAutoSpeaker &&
      message.speakText &&
      !isSpeaked &&
      !isSpeakTextReady
    ) {
      if (isMuted) {
        setIsSpeakTextReady(true);
        setStartOnMute(true);
        return;
      }

      const { lang, text, inputFormat } = getSsmlFormat(
        message.speakText,
        TutorChatL1.EN,
        ttsVoices
      );

      const azureTTS = new AzureTTS({
        autoPlay: false,
        inputFormat: inputFormat,
        language: lang,
        voice: voice,
        getLipsync: true,
        audio: TutorTopicsUtils.getGlobalAudio(),
      });
      azureTTS.tts(text).finally(() => {
        if (isActiveRef.current) {
          setIsSpeakTextReady(true);
        }
      });
    }
  }, [
    message.speakText,
    message.disableAutoSpeaker,
    isSpeaked,
    isSpeakTextReady,
    voice,
    showSpeaker,
    isMuted,
    ttsVoices,
  ]);

  useEffect(() => {
    if (
      message.speakText &&
      !message.disableAutoSpeaker &&
      !isSpeaked &&
      !startOnMute &&
      isSpeakTextReady
    ) {
      handleSpeak(message.speakText, TutorChatL1.EN, false);
    }
  }, [
    message.speakText,
    message.disableAutoSpeaker,
    isSpeaked,
    startOnMute,
    isSpeakTextReady,
    handleSpeak,
  ]);

  const isUser = useMemo(() => {
    return message.sender === TutorChatSender.USER;
  }, [message.sender]);

  const isWriting = useMemo(() => {
    return (
      message.status === 'writing' ||
      (!message.disableAutoSpeaker && showSpeaker && message.speakText && !isSpeakTextReady)
    );
  }, [
    message.status,
    message.speakText,
    message.disableAutoSpeaker,
    isSpeakTextReady,
    showSpeaker,
  ]);

  const messagesWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onWritingChange?.(
      !!isWriting,
      messagesWrapperRef.current,
      !isWriting && (!!topicsGroups?.length || !!quisGroups?.length)
    );
  }, [isWriting]);

  return (
    <>
      <div
        ref={messagesWrapperRef}
        className={classes(styles.messagesWrapper, {
          [styles.messagesWrapperUser]: isUser,
          [styles.messagesWrapperTutor]: !isUser,
          [styles.messagesWrapperTutorWithoutIcon]: !isUser && !showTuorIcon,
        })}
      >
        {!isUser && showTuorIcon && (
          <div className={styles.avatar}>
            {/*<SVG className={styles.robotIcon} src={robotIcon} />*/}
            <TutorAvatarIcon className={styles.robotIcon} animate={false} />
          </div>
        )}

        {isWriting && (
          <div className={classes(styles.message, styles.firstMessage, styles.isWriting)}>
            <div className={styles.typing}>
              <div className={styles.dot}></div>
              <div className={styles.dot}></div>
              <div className={styles.dot}></div>
            </div>
          </div>
        )}

        {!isWriting && (
          <>
            <div
              className={classes(styles.message, styles.firstMessage, {
                [styles.isDiffsMessage]: !!message.textWithDiffs,
              })}
            >
              <div
                className={styles.messageText}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(message.textWithDiffs || message.text || ''),
                }}
              />
              {message.speakText && getSpeakerIcon(message.speakText || '', TutorChatL1.EN)}
            </div>

            <div className={styles.extraData}>
              {message.hasCorrectedVersion && message.corrected && (
                <div className={styles.messageWrapper}>
                  <div className={styles.message}>
                    <div className={styles.messageText}>{message.corrected?.text || ''}</div>
                  </div>
                  {message.corrected?.explanation && (
                    <SVG
                      className={styles.showCorrectionIcon}
                      src={lightbulbIcon}
                      onClick={() => setShowCorrection(!showCorrection)}
                    />
                  )}
                </div>
              )}

              {showCorrection && (
                <div className={classes(styles.message, styles.isExplanation)}>
                  <SVG className={styles.showCorrectionIcon} src={lightbulbIcon} />
                  <div className={styles.messageText}>{message.corrected?.explanation}</div>
                  {getSpeakerIcon(
                    message.corrected?.explanation || '',
                    message.corrected?.explanation_language || TutorChatL1.EN
                  )}
                </div>
              )}

              {showNameAndTime && (
                <div className={styles.nameAndTime}>
                  <div>{message.sender == 'tutor' ? 'Private Tutor[AI-generated]' : userName}</div>
                  <div className={styles.nameAndTimeChild} />
                  <div>{getTime(message.dateTime)}</div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {!isWriting && topicsGroups && (
        <div className={styles.topicsGroups}>
          {topicsGroups.map((group, groupIndex) => {
            const title = group.title || '';
            const topics = group.topics || [];

            return (
              <div key={`topicGroup--${groupIndex}`} className={styles.topicGroup}>
                {title && <div className={styles.topicGroupTitle}>{title}</div>}
                <div className={styles.topicslayout}>
                  {topics.map((topic, topicIndex) => (
                    <button
                      key={`topic--${groupIndex}--${topicIndex}`}
                      className={styles.topicButton}
                      onClick={() => onTopicClick(topic)}
                    >
                      {topic.text}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isWriting && quisGroups && (
        <QuiFromDashboard quisGroups={quisGroups} onQuizComplete={onQuizComplete} />
      )}
    </>
  );
};

export default MessageItem;
